import { useTranslation } from "react-i18next";

import type { Chase } from "@/campaigns/modules/chase/types";
import { chasePhase, spinBudget } from "@/campaigns/modules/chase/types";
import { MissionTaskRow } from "@/components/campaigns/missions/mission-task-row";
import { Card } from "@/components/ui/card";

/**
 * The targets the run is made of, as the mission task rows (one task anatomy everywhere), under
 * one line that says what they are for. Every open target is playable at once — a chase has no
 * "current" one — so a row is either `done` or `neutral`, never the ladder's highlight / up-next
 * pair. Progress (figure + meter) only once the player's push carries per-target state; before
 * that the rows are the structural list alone.
 */
export const ChaseTargetsCard = ({
  chase,
  className,
}: {
  chase: Chase;
  className?: string;
}) => {
  const { t } = useTranslation();
  const live = chase.progress?.hasTargetState === true;
  const phase = chasePhase(chase);
  const open = phase === "chasing";
  const budget = spinBudget(chase);
  const total = chase.targets.length;

  if (total === 0) return null;

  return (
    <Card
      inset="none"
      title={t("chase.targetsHeading", "Targets")}
      titleRowClassName="pb-1"
      className={className}
    >
      {budget !== null && (
        <p className="px-(--card-pad) pb-2 font-body text-[12px] leading-4 text-foreground-secondary lg:text-[13px] lg:leading-[18px]">
          {t(
            "chase.targetsIntro",
            "Complete all targets within {{count}} spins to win.",
            { count: budget }
          )}
        </p>
      )}
      <div className="flex w-full flex-col px-(--card-pad) pb-1 lg:pb-2">
        {chase.targets.map((target) => (
          <MissionTaskRow
            key={target.id}
            task={target}
            variant={target.isCompleted ? "done" : "neutral"}
            showProgress={live && open}
            showContributionNote={open}
          />
        ))}
      </div>
    </Card>
  );
};
